import React, { useState, useEffect } from 'react';
import { createUser, listRoles } from '../../api/admin';
import { getCountryFlag } from '../../util/storage';

export default function CreateUserModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone_number: '',
    address: '',
    country_code: '',
    region: 'global',
    roles: ['viewer'],
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const data = await listRoles();
      setAvailableRoles(data.roles || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.password || !formData.full_name || !formData.email) {
      setError('Username, password, full name, and email are required');
      return;
    }

    if (formData.roles.length === 0) {
      setError('At least one role must be selected');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Build request with only non-empty optional fields
      const userData = {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        email: formData.email,
        region: formData.region,
        roles: formData.roles,
      };

      if (formData.phone_number) userData.phone_number = formData.phone_number;
      if (formData.address) userData.address = formData.address;
      if (formData.country_code) userData.country_code = formData.country_code;

      await createUser(userData);
      onCreate(); // Notify parent
    } catch (err) {
      console.error('Failed to create user:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleToggle = (roleName) => {
    if (formData.roles.includes(roleName)) {
      setFormData({
        ...formData,
        roles: formData.roles.filter((r) => r !== roleName),
      });
    } else {
      setFormData({
        ...formData,
        roles: [...formData.roles, roleName],
      });
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal">
        <div className="modal-header">
          <h3>Create New User</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
              />
              <small>Minimum 6 characters</small>
            </div>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Country Code</label>
              <div className="country-input-wrapper">
                <span className="country-input-flag">{getCountryFlag(formData.country_code)}</span>
                <input
                  type="text"
                  value={formData.country_code}
                  onChange={(e) => setFormData({ ...formData, country_code: e.target.value.toUpperCase() })}
                  maxLength={2}
                  placeholder="US, CA, GB..."
                  className="country-input"
                />
              </div>
              <small>2-letter country code (optional)</small>
            </div>

            <div className="form-group">
              <label>Region *</label>
              <select
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                required
              >
                <option value="global">Global</option>
                <option value="north-america">North America</option>
                <option value="europe">Europe</option>
                <option value="asia-pacific">Asia Pacific</option>
              </select>
            </div>

            <div className="form-group">
              <label>Roles *</label>
              <div className="role-checkboxes">
                {availableRoles.map((role) => (
                  <label key={role.name} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role.name)}
                      onChange={() => handleRoleToggle(role.name)}
                    />
                    <span>{role.name}</span>
                    <small>{role.description}</small>
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating...' : 'Create User'}
              </button>
              <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
